// src/pages/admin/UserView.jsx
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchUsers } from "../services/api";
import AdminLayout from "../components/layout/AdminLayout";
import { Button } from "../components/ui/button";
import { FaEdit } from "react-icons/fa";

const AdminUserView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: response, isLoading } = useQuery({
    queryKey: ["hospitalUsers"],
    queryFn: fetchUsers,
  });

  const users = response?.users || response?.data || response?.results || [];
  const user = users.find((u) => u.id === id || u.id === Number(id));


  const getInitials = (name = "") =>
    name
      .trim()
      .split(/\s+/)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "NA";

  if (isLoading) return <AdminLayout><div className="p-10 text-center">Loading...</div></AdminLayout>;
  if (!user) return <AdminLayout><div className="p-10 text-center">User not found</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold">User Details</h1>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate(`/admin/users/${id}/edit`)}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              <FaEdit /> Edit User
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/users")}>
              Back to List
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-5 pb-6 border-b">
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-2xl font-bold">
              {getInitials(user.name || user.full_name)}
            </div>
            <div>
              <h2 className="text-2xl font-bold">
                {user.name || user.full_name || "—"}
              </h2>
              <p className="text-gray-600">{user.email || "—"}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 uppercase mb-1">Phone Number</label>
              <p className="text-gray-900">{user.phone || user.phone_number || "—"}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 uppercase mb-1">Patient ID</label>
              <p className="text-gray-900">{user.patientId || user.hospital_number || "—"}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 uppercase mb-1">Status</label>
              <span
                className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                  user.status?.toLowerCase() === "active"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : "Unknown"}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 uppercase mb-1">Date Joined</label>
              <p className="text-gray-900">
                {user.created_at || user.joined
                  ? new Date(user.created_at || user.joined).toLocaleDateString()
                  : "—"}
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-500 uppercase mb-1">Wallet Balance</label>
              <p className="text-2xl font-bold text-green-700">
                ₦{parseFloat(user.wallet_balance || user.wallet?.balance || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUserView;